import { Callback1, MutableMapStore, Store, StoreObserver, VoidCallback } from '@deqo/foundation';
import { debounce } from '../../../CodesThatsGottaGoSomewhereElse';
import { defineCommand } from '../../../concepts/commands/CommandDefinition';
import { CommandDispatcher } from '../../../concepts/commands/CommandDispatcher';
import { CommandRegistry } from '../../../concepts/commands/CommandRegistry';
import { ObjectManager } from '../../../concepts/objects';
import { ActiveObject, resolve } from '../../Container';
import { sendAlert } from '../alerts/AlertCommands';
import { Feature } from './Feature';

export type FeatureCallback = (enabled: boolean) => void;

export class FeatureManager extends ActiveObject implements Store<ReadonlyMap<string, boolean>> {

    @resolve(CommandRegistry) private commands!: CommandRegistry;
    @resolve(CommandDispatcher) private dispatch!: CommandDispatcher;
    @resolve(ObjectManager) private objects!: ObjectManager;

    private callbacks = new Map<string, FeatureCallback>();
    private store = new MutableMapStore<string, boolean>();

    public start() {
        const dsync = debounce(this.sync);
        return this.burden(
            this.objects.on('Feature', 'affected', dsync),
            this.objects.on('Feature', 'allocated', dsync),
            this.objects.on('Feature', 'released', dsync),
        );
    }

    public define(name: string, callback: FeatureCallback) {
        const feature = this.resolveFeature(name);
        const enabled = feature?.enabled ?? false;
        this.callbacks.set(name, callback);
        this.store.mutate(x => void (x.set(name, enabled)));
        this.installCommand(name);
        callback(enabled);
        return () => {
            this.callbacks.delete(name);
            this.store.mutate(x => void (x.delete(name)));
        };
    }

    public get(name: string) {
        return this.store.get(name);
    }

    public set(name: string, enabled: boolean) {
        this.objects.update(u => {
            const feature = this.resolveFeature(name) ?? u.create(Feature);
            feature.name = name;
            feature.enabled = enabled;
        });
        this.dispatch(sendAlert, [`${name} feature is now *${enabled ? 'ON' : 'OFF'}*.`]);
    }

    public toggle(name: string) {
        this.set(name, !(this.resolveFeature(name)?.enabled ?? false));
    }

    public getState() {
        return this.store.getState();
    }

    public observe(observer: StoreObserver<ReadonlyMap<string, boolean>>): VoidCallback;
    public observe<P>(path: Callback1<P, ReadonlyMap<string, boolean>>, observer: StoreObserver<P>): VoidCallback;
    public observe(path: any, observer?: any): VoidCallback {
        return this.store.observe(path, observer);
    }

    private sync = () => {
        for (const [name, prev] of this.store.getState()) {
            const next = this.resolveFeature(name)?.enabled ?? false;
            if (prev !== next) {
                this.store.mutate(x => void (x.set(name, next)));
                this.callbacks.get(name)(next);
            }
        }
    };

    private installCommand(name: string) {
        const key = `toggle${name}FeatureState`;
        this.commands.remove(key);
        if (this.store.has(name)) {
            this.commands.add(defineCommand(
                key,
                `Feature: Toggle ${name} On/Off`,
                r => r.call(FeatureManager).toggle(name),
            ));
        }
    }

    private resolveFeature(name: string) {
        // Potentially, due to "realtimeness" more than one entry could exist, so sort on id so clients always choose the same one.
        return Array.from(this.objects.getAll(Feature))
            .sort((a, b) => a.id.localeCompare(b.id))
            .find(x => !!x.alive && x.name === name) || null;
    }
}
